import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { TextAreaProps } from '@progress/kendo-react-inputs';
import { FieldWrapper } from '@progress/kendo-react-form';
import { TextArea } from '@progress/kendo-react-inputs';
import { Label, Error, Hint } from '@progress/kendo-react-labels';

interface TextareaStateWrapperProps<T extends FieldValues>
  extends UseControllerProps<T>, Omit<TextAreaProps, 'defaultValue' | 'name'> {
  label?: string;
  disabled?: boolean;
  hint?: string;
  type?: string;
  placeholder?: string;
  inverted?: boolean;
  wrapperClassName?: string;
  required?: boolean;
}

export const TextareaStateWrapper = <T extends FieldValues>({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  disabled,
  hint,
  wrapperClassName,
  label,
  required,
  ...rest
}: TextareaStateWrapperProps<T>) => {
  const {
    field,
    fieldState: { error, isTouched },
  } = useController({
    name,
    rules,
    shouldUnregister,
    defaultValue,
    control,
    disabled,
  });

  const id = field.name;
  const showRequired = required ?? Boolean(rules?.required);
  const showValidationMessage = Boolean(isTouched && error?.message);
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${id}_hint` : '';
  const errorId = showValidationMessage ? `${id}_error` : '';

  return (
    <FieldWrapper className={wrapperClassName}>
      {label && (
        <Label
          editorId={id}
          editorValid={!showValidationMessage}
          editorDisabled={disabled}
          className="k-form-label"
        >
          {label} {showRequired ? <span className="text-sm">*</span> : ''}
        </Label>
      )}
      <div className={'k-form-field-wrap'}>
        <TextArea required={required} {...field} {...rest} />

        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{error?.message}</Error>}
      </div>
    </FieldWrapper>
  );
};
